// components/Dashboard/UsersManagement.js
import React, { useState, useEffect } from "react";
import { usersAPI } from "./api";
import {
  Container,
  StatsContainer,
  StatCard,
  StatValue,
  StatLabel,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  RoleBadge,
  ActionsContainer,
  ActionButton,
  LoadingContainer,
  ErrorMessage,
  SuccessMessage,
  EmptyState,
  DateText,
  EmailText,
  UsernameText,
  FilterContainer,
  FilterButton,
} from "./UsersManagement.styles";

const UsersManagement = ({ userDate, isDarkMode }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setisAdmin] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [processing, setProcessing] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await usersAPI.getAllUsers();
      setUsers(data.users || data);
      const isAdminUser =
        data.users?.filter(
          (u) => u.email === userDate?.email && u.role === "admin"
        ).length > 0;

      setisAdmin(isAdminUser);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    console.log(userId);

    setProcessing((prev) => ({ ...prev, [userId]: "approving" }));
    setError("");
    try {
      const result = await usersAPI.approveUser(userId);
      setSuccess(result.message);

      // Update user in state
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, accountApproved: true } : user
        )
      );

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve user");
    } finally {
      setProcessing((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleDisapprove = async (userId) => {
    setProcessing((prev) => ({ ...prev, [userId]: "disapproving" }));
    setError("");
    try {
      const result = await usersAPI.disapproveUser(userId);
      setSuccess(result.message);

      // Update user in state
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, accountApproved: false } : user
        )
      );

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to disapprove user");
    } finally {
      setProcessing((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleDelete = async (userId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone."
      )
    ) {
      return;
    }

    setProcessing((prev) => ({ ...prev, [userId]: "deleting" }));
    setError("");
    try {
      await usersAPI.deleteUser(userId);
      const remainingUsers = users?.filter((user) => user.id !== userId);
      setUsers(remainingUsers || users);
      setSuccess("User deleted successfully.");
    } catch (err) {
      setError(err.response?.data?.message || "Delete feature not available");
    } finally {
      setProcessing((prev) => ({ ...prev, [userId]: false }));
      setTimeout(() => {
        setError("");
        setSuccess("");
      }, 4000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Filter users based on selected filter
  const filteredUsers = users.filter((user) => {
    switch (filter) {
      case "pending":
        return !user.accountApproved && user.role !== "admin";
      case "approved":
        return user.accountApproved && user.role !== "admin";
      case "admin":
        return user.role === "admin";
      default:
        return true;
    }
  });

  // Calculate stats
  const stats = {
    total: users.length,
    pending: users.filter((u) => !u.accountApproved && u.role !== "admin")
      .length,
    approved: users.filter((u) => u.accountApproved && u.role !== "admin")
      .length,
    admins: users.filter((u) => u.role === "admin").length,
  };

  if (loading) {
    return <Container>↻ Loading...</Container>;
  }
  if (!isAdmin) {
    return (
      <Container>
        <ErrorMessage>
          <span>⚠️</span>
          Access Denied: You must be an administrator to view this page.
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container id={isDarkMode ? "dark-mode" : ""}>
      {/* <Header>
        <Title></Title>
        <RefreshButton onClick={loadUsers} disabled={loading}>
          {loading ? "↻ Loading..." : "↻ Refresh"}
        </RefreshButton>
      </Header> */}
      <div className="text-center mb-4">{loading ? "↻ Loading..." : ""}</div>
      {error && (
        <ErrorMessage>
          <span>⚠️</span>
          {error}
        </ErrorMessage>
      )}

      {success && (
        <SuccessMessage>
          <span>✓</span>
          {success}
        </SuccessMessage>
      )}

      <StatsContainer>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.pending}</StatValue>
          <StatLabel>Pending Approval</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.approved}</StatValue>
          <StatLabel>Approved Users</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.admins}</StatValue>
          <StatLabel>Administrators</StatLabel>
        </StatCard>
      </StatsContainer>

      <FilterContainer>
        <FilterButton
          active={filter === "all"}
          onClick={() => setFilter("all")}
          className={filter === "all" ? "active-button" : ""}
        >
          All Users
        </FilterButton>
        <FilterButton
          active={filter === "pending"}
          onClick={() => setFilter("pending")}
          className={filter === "pending" ? "active-button" : ""}
        >
          Pending Approval
        </FilterButton>
        <FilterButton
          active={filter === "approved"}
          onClick={() => setFilter("approved")}
          className={filter === "approved" ? "active-button" : ""}
        >
          Approved Users
        </FilterButton>
        <FilterButton
          active={filter === "admin"}
          onClick={() => setFilter("admin")}
          className={filter === "admin" ? "active-button" : ""}
        >
          Administrators
        </FilterButton>
      </FilterContainer>

      {loading ? (
        <LoadingContainer>Loading users...</LoadingContainer>
      ) : filteredUsers.length === 0 ? (
        <EmptyState>
          <h3>No users found</h3>
          <p>Try changing your filter or check back later.</p>
        </EmptyState>
      ) : (
        <TableContainer style={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Email</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Role</TableHeader>
                <TableHeader>Joined</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {filteredUsers.map((user) => {
                const isProcessing = processing[user.id];
                const isCurrentAdmin = user.role === "admin";

                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <UsernameText>{user.username}</UsernameText>
                    </TableCell>{" "}
                    <TableCell>
                      <EmailText>{user.email}</EmailText>
                    </TableCell>
                    <TableCell>
                      {isCurrentAdmin ? (
                        <StatusBadge status="admin">Approved</StatusBadge>
                      ) : user.accountApproved ? (
                        <StatusBadge status="approved">Approved</StatusBadge>
                      ) : (
                        <StatusBadge status="pending">Pending</StatusBadge>
                      )}
                    </TableCell>
                    <TableCell>
                      <RoleBadge role={user.role}>{user.role}</RoleBadge>
                    </TableCell>
                    <TableCell>
                      <DateText>{formatDate(user.createdAt)}</DateText>
                    </TableCell>
                    <TableCell>
                      <ActionsContainer>
                        {!isCurrentAdmin && !user.accountApproved && (
                          <ActionButton
                            variant="approve"
                            onClick={() => handleApprove(user.id)}
                            disabled={isProcessing}
                          >
                            {isProcessing === "approving" ? "..." : "Approve"}
                          </ActionButton>
                        )}

                        {!isCurrentAdmin && user.accountApproved && (
                          <ActionButton
                            variant="disapprove"
                            onClick={() => handleDisapprove(user.id)}
                            disabled={isProcessing}
                          >
                            {isProcessing === "disapproving"
                              ? "..."
                              : "Disapprove"}
                          </ActionButton>
                        )}
                        {!isCurrentAdmin && (
                          <ActionButton
                            variant="delete"
                            onClick={() => handleDelete(user.id)}
                            disabled={isProcessing}
                          >
                            {isProcessing === "deleting" ? "..." : "Delete"}
                          </ActionButton>
                        )}

                        {isCurrentAdmin && (
                          <span
                            style={{ color: "#b0b0b0", fontSize: "0.8rem" }}
                          >
                            Auto-approved
                          </span>
                        )}
                      </ActionsContainer>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default UsersManagement;
